import { ReactNode, useRef, useState, useEffect, useCallback } from 'react'
import { StyleSheet, View, ViewProps, ViewStyle, TextStyle, StyleProp } from 'react-native'
import { createPortal } from 'react-dom'
import { Color, Radius, Spacing, Text } from 'zens-storybook'

const DEFAULT_BACKGROUND = 'rgb(169, 158, 151)'
const DEFAULT_ARROW_SIZE = 6

export type TooltipPlacement = 'top' | 'bottom'

interface TooltipProps extends ViewProps {
  label: ReactNode
  visible?: boolean
  placement?: TooltipPlacement
  offset?: number
  backgroundColor?: string
  showArrow?: boolean
  tooltipStyle?: StyleProp<ViewStyle>
  textStyle?: StyleProp<TextStyle>
  arrowStyle?: StyleProp<ViewStyle>
  containerStyle?: StyleProp<ViewStyle>
  children: ReactNode
  truncateLabel?: boolean
}

const Tooltip = (props: TooltipProps) => {
  const {
    label,
    visible = false,
    placement = 'top',
    offset = Spacing.XS,
    backgroundColor = DEFAULT_BACKGROUND,
    showArrow = true,
    tooltipStyle,
    textStyle,
    arrowStyle,
    containerStyle,
    style,
    children,
    truncateLabel = true,
    ...otherProps
  } = props

  const triggerRef = useRef<View>(null)
  const [position, setPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null)

  const measureTrigger = useCallback(() => {
    if (triggerRef.current) {
      const element = triggerRef.current as unknown as HTMLElement
      if (element.getBoundingClientRect) {
        const rect = element.getBoundingClientRect()
        setPosition({
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX + rect.width / 2,
          width: rect.width,
          height: rect.height,
        })
      }
    }
  }, [])

  useEffect(() => {
    if (visible) {
      measureTrigger()

      // Re-measure on resize and scroll so tooltip stays anchored
      window.addEventListener('resize', measureTrigger)
      window.addEventListener('scroll', measureTrigger, true) // capture phase for nested scrolls

      return () => {
        window.removeEventListener('resize', measureTrigger)
        window.removeEventListener('scroll', measureTrigger, true)
      }
    } else if (!visible) {
      setPosition(null)
    }
  }, [visible, measureTrigger])

  const arrowColorStyle =
    placement === 'top'
      ? { borderTopColor: backgroundColor }
      : { borderBottomColor: backgroundColor }

  // Tooltip bubble content (shared between inline and portal)
  const tooltipContent = (
    <>
      {placement === 'bottom' && showArrow && (
        <View style={[styles.arrowBase, styles.arrowUp, arrowColorStyle, arrowStyle]} />
      )}
      <View style={[styles.tooltipBubble, { backgroundColor }, tooltipStyle]}>
        <Text numberOfLines={truncateLabel ? 1 : undefined} style={[styles.text, textStyle]}>
          {label}
        </Text>
      </View>
      {placement === 'top' && showArrow && (
        <View style={[styles.arrowBase, styles.arrowDown, arrowColorStyle, arrowStyle]} />
      )}
    </>
  )

  // Portal tooltip for web - renders outside the DOM hierarchy
  const renderPortalTooltip = () => {
    if (!visible || !position) return null

    const portalStyle: React.CSSProperties = {
      position: 'fixed',
      top: placement === 'top' ? position.top - offset : position.top + position.height + offset,
      left: position.left,
      transform: `translate(-50%, ${placement === 'top' ? '-100%' : '0'})`,
      zIndex: 99999,
      pointerEvents: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }

    return createPortal(
      <div style={portalStyle}>{tooltipContent}</div>,
      document.body
    )
  }

  return (
    <View ref={triggerRef} style={[styles.wrapper, style]} {...otherProps}>
      {children}
      {renderPortalTooltip()}
    </View>
  )
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative',
    display: 'flex',
  },
  tooltipContainer: {
    position: 'absolute',
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    zIndex: 1000,
  },
  tooltipTopContainer: {
    bottom: '100%',
    flexDirection: 'column',
  },
  tooltipBottomContainer: {
    top: '100%',
    flexDirection: 'column-reverse',
  },
  tooltipBubble: {
    paddingVertical: 4,
    paddingHorizontal: Spacing.S,
    borderRadius: Radius.S,
    maxWidth: 240,
  },
  text: {
    color: Color.simplyWhite,
    fontSize: 12,
  },
  arrowBase: {
    width: 0,
    height: 0,
    borderLeftWidth: DEFAULT_ARROW_SIZE,
    borderRightWidth: DEFAULT_ARROW_SIZE,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },
  arrowDown: {
    borderTopWidth: DEFAULT_ARROW_SIZE,
  },
  arrowUp: {
    borderBottomWidth: DEFAULT_ARROW_SIZE,
  },
})

export default Tooltip